import React, { useState, useEffect } from 'react';

const ValentinesDaySceneOverlay = () => {
  const [floatingHearts, setFloatingHearts] = useState([]);
  const [rosePetals, setRosePetals] = useState([]);

  useEffect(() => {
    // Create floating hearts
    const heartCount = 15;
    const initialHearts = Array.from({ length: heartCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 15 + Math.random() * 25,
      duration: 10 + Math.random() * 8,
      delay: Math.random() * 5,
    }));
    setFloatingHearts(initialHearts);

    // Create falling rose petals
    const petalCount = 40;
    const initialPetals = Array.from({ length: petalCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      delay: Math.random() * 10,
      duration: 12 + Math.random() * 8,
      size: 8 + Math.random() * 12,
    }));
    setRosePetals(initialPetals);
  }, []);

  return (
    <div className="valentines-scene-overlay">
      <style>
        {`
        .valentines-scene-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 0;
          pointer-events: none;
          overflow: hidden;
        }

        /* Romantic gradient sky */
        .romantic-sky {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            to bottom,
            #ffe0f0 0%,
            #ffc0e0 20%,
            #ffb0d8 40%,
            #ffa0d0 60%,
            #ff90c8 80%,
            #ff80c0 100%
          );
          z-index: 0;
        }

        /* Soft romantic glow */
        .romantic-glow {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
            ellipse at 50% 30%,
            rgba(255, 182, 193, 0.4) 0%,
            transparent 60%
          );
          z-index: 1;
        }

        /* Cherry blossom trees */
        .cherry-tree {
          position: absolute;
          bottom: 0;
          z-index: 2;
        }

        .tree-trunk {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 40px;
          height: 250px;
          background: linear-gradient(90deg, #4a3025 0%, #6b4d3b 50%, #4a3025 100%);
          border-radius: 20px 20px 0 0;
          box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
        }

        .tree-branch {
          position: absolute;
          background: linear-gradient(90deg, #6b4d3b 0%, #8b6d5b 50%, #6b4d3b 100%);
          border-radius: 50px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        /* Cherry blossoms */
        .blossom-cluster {
          position: absolute;
          animation: blossomSway ease-in-out infinite;
        }

        @keyframes blossomSway {
          0%, 100% { transform: rotate(-2deg); }
          50% { transform: rotate(2deg); }
        }

        .blossom-flower {
          position: absolute;
          width: 25px;
          height: 25px;
        }

        .blossom-petal {
          position: absolute;
          width: 12px;
          height: 12px;
          background: radial-gradient(ellipse at center, #ffb6c1 0%, #ffb0c8 50%, #ff9db8 100%);
          border-radius: 50% 0 50% 0;
          box-shadow: 0 0 8px rgba(255, 182, 193, 0.6);
        }

        .blossom-center {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 6px;
          height: 6px;
          background: radial-gradient(circle, #ffd700 0%, #ffb347 100%);
          border-radius: 50%;
          box-shadow: 0 0 5px rgba(255, 215, 0, 0.8);
        }

        /* Floating hearts */
        .floating-heart {
          position: absolute;
          animation: heartFloat ease-in-out infinite;
          filter: drop-shadow(0 2px 6px rgba(255, 105, 180, 0.4));
        }

        @keyframes heartFloat {
          0%, 100% { 
            transform: translateY(0) rotate(0deg);
            opacity: 0.7;
          }
          25% { 
            transform: translateY(-15px) rotate(5deg);
            opacity: 0.9;
          }
          50% { 
            transform: translateY(-25px) rotate(0deg);
            opacity: 1;
          }
          75% { 
            transform: translateY(-15px) rotate(-5deg);
            opacity: 0.9;
          }
        }

        .heart-shape {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #ff6b9d 0%, #ff1493 50%, #c71585 100%);
          clip-path: path('M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z');
        }

        /* Rose petals falling */
        .rose-petal {
          position: absolute;
          animation: petalFall linear infinite;
          transform-origin: center;
        }

        @keyframes petalFall {
          0% {
            transform: translateY(-20px) rotate(0deg) translateX(0);
            opacity: 1;
          }
          50% {
            transform: translateY(50vh) rotate(180deg) translateX(30px);
            opacity: 0.8;
          }
          100% {
            transform: translateY(100vh) rotate(360deg) translateX(0);
            opacity: 0.3;
          }
        }

        .petal-shape {
          width: 100%;
          height: 100%;
          background: radial-gradient(ellipse at 30% 30%, #ffb6c1 0%, #ff9db8 60%, #ff6b9d 100%);
          border-radius: 50% 0 50% 0;
          box-shadow: 0 2px 6px rgba(255, 105, 180, 0.3);
        }

        /* Romantic candles */
        .romantic-candle-setup {
          position: absolute;
          bottom: 20%;
          left: 50%;
          transform: translateX(-50%);
          z-index: 5;
        }

        .candle-group {
          position: relative;
          display: flex;
          gap: 40px;
        }

        .romantic-candle {
          position: relative;
          width: 20px;
          height: 60px;
        }

        .candle-wax {
          width: 100%;
          height: 50px;
          background: linear-gradient(90deg, #ffe4e1 0%, #fff5f5 50%, #ffe4e1 100%);
          border-radius: 3px 3px 0 0;
          box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
        }

        .candle-flame-romantic {
          position: absolute;
          top: -18px;
          left: 50%;
          transform: translateX(-50%);
          width: 14px;
          height: 20px;
          background: linear-gradient(to top, #ff6b35 0%, #ff8c42 30%, #ffa552 60%, #ffd700 100%);
          border-radius: 50% 50% 0 0;
          filter: blur(3px);
          animation: romanticFlameFlicker 2s ease-in-out infinite;
          box-shadow: 0 0 20px rgba(255, 165, 0, 0.8);
        }

        @keyframes romanticFlameFlicker {
          0%, 100% { transform: translateX(-50%) scaleY(1); opacity: 0.95; }
          50% { transform: translateX(-50%) scaleY(1.2); opacity: 1; }
        }

        /* Rose bouquet */
        .rose-bouquet {
          position: absolute;
          bottom: 15%;
          right: 12%;
          width: 150px;
          height: 180px;
          z-index: 4;
        }

        .rose {
          position: absolute;
          width: 40px;
          height: 50px;
        }

        .rose-bloom {
          position: absolute;
          bottom: 15px;
          left: 50%;
          transform: translateX(-50%);
          width: 35px;
          height: 35px;
          background: radial-gradient(circle at 30% 30%, #ff6b9d 0%, #ff1493 50%, #c71585 100%);
          border-radius: 50% 40% 50% 40%;
          box-shadow: 0 3px 10px rgba(255, 20, 147, 0.5);
          animation: roseBloom 4s ease-in-out infinite;
        }

        @keyframes roseBloom {
          0%, 100% { transform: translateX(-50%) scale(1); }
          50% { transform: translateX(-50%) scale(1.08); }
        }

        .rose-stem {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 4px;
          height: 50px;
          background: linear-gradient(180deg, #228b22 0%, #006400 100%);
          border-radius: 2px;
        }

        .rose-leaf {
          position: absolute;
          width: 15px;
          height: 10px;
          background: #228b22;
          border-radius: 50% 0 50% 0;
          transform: rotate(45deg);
        }

        /* Romantic text/love notes floating */
        .love-note {
          position: absolute;
          width: 100px;
          height: 80px;
          background: linear-gradient(135deg, #fff0f5 0%, #ffe4e8 100%);
          border-radius: 5px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          animation: noteFloat ease-in-out infinite;
          z-index: 3;
        }

        @keyframes noteFloat {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50% { transform: translateY(-20px) rotate(2deg); }
        }

        .note-heart-doodle {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 30px;
          height: 30px;
          background: linear-gradient(135deg, #ff69b4 0%, #ff1493 100%);
          clip-path: path('M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z');
          opacity: 0.6;
        }

        /* Heart-shaped balloon */
        .heart-balloon {
          position: absolute;
          width: 80px;
          height: 80px;
          animation: balloonFloat ease-in-out infinite;
          z-index: 6;
        }

        @keyframes balloonFloat {
          0%, 100% { 
            transform: translateY(0) rotate(-3deg);
          }
          50% { 
            transform: translateY(-30px) rotate(3deg);
          }
        }

        .balloon-heart {
          width: 100%;
          height: 100%;
          background: radial-gradient(ellipse at 40% 40%, #ff69b4 0%, #ff1493 50%, #c71585 100%);
          clip-path: path('M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z');
          box-shadow: 0 10px 30px rgba(255, 20, 147, 0.4);
        }

        .balloon-string {
          position: absolute;
          bottom: -40px;
          left: 50%;
          transform: translateX(-50%);
          width: 2px;
          height: 40px;
          background: linear-gradient(180deg, #fff 0%, transparent 100%);
        }

        /* Sparkles */
        .sparkle {
          position: absolute;
          width: 6px;
          height: 6px;
          background: #ffffff;
          border-radius: 50%;
          animation: sparkleShine ease-in-out infinite;
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.9);
        }

        @keyframes sparkleShine {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }

        /* Ground with rose petals */
        .romantic-ground {
          position: absolute;
          bottom: 0;
          width: 100%;
          height: 15%;
          background: linear-gradient(
            to bottom,
            rgba(255, 228, 225, 0.5) 0%,
            rgba(255, 240, 245, 0.8) 50%,
            rgba(255, 250, 250, 0.95) 100%
          );
          z-index: 10;
          box-shadow: 0 -10px 30px rgba(255, 182, 193, 0.3);
        }

        .ground-petal-scatter {
          position: absolute;
          width: 15px;
          height: 12px;
          background: radial-gradient(ellipse, #ffb6c1 0%, #ff9db8 100%);
          border-radius: 50% 0 50% 0;
          opacity: 0.7;
        }
        `}
      </style>

      {/* Romantic gradient sky */}
      <div className="romantic-sky" />

      {/* Romantic glow overlay */}
      <div className="romantic-glow" />

      {/* Left cherry blossom tree */}
      <div className="cherry-tree" style={{ left: '5%' }}>
        <div className="tree-trunk" />
        {/* Branches */}
        <div className="tree-branch" style={{ bottom: '180px', left: '10px', width: '120px', height: '15px', transform: 'rotate(-25deg)' }} />
        <div className="tree-branch" style={{ bottom: '150px', left: '25px', width: '100px', height: '12px', transform: 'rotate(-35deg)' }} />
        <div className="tree-branch" style={{ bottom: '120px', left: '15px', width: '90px', height: '10px', transform: 'rotate(-30deg)' }} />
        
        {/* Blossom clusters on branches */}
        {[
          { x: 50, y: 190, delay: 0 },
          { x: 75, y: 185, delay: 0.5 },
          { x: 95, y: 175, delay: 1 },
          { x: 65, y: 160, delay: 1.5 },
          { x: 85, y: 155, delay: 2 },
          { x: 55, y: 130, delay: 2.5 },
          { x: 75, y: 125, delay: 3 },
        ].map((cluster, i) => (
          <div
            key={i}
            className="blossom-cluster"
            style={{
              left: `${cluster.x}px`,
              bottom: `${cluster.y}px`,
              animationDuration: '4s',
              animationDelay: `${cluster.delay}s`,
            }}
          >
            <div className="blossom-flower">
              <div className="blossom-petal" style={{ top: '-6px', left: '6px', transform: 'rotate(0deg)' }} />
              <div className="blossom-petal" style={{ top: '2px', left: '12px', transform: 'rotate(72deg)' }} />
              <div className="blossom-petal" style={{ top: '10px', left: '8px', transform: 'rotate(144deg)' }} />
              <div className="blossom-petal" style={{ top: '10px', left: '0px', transform: 'rotate(216deg)' }} />
              <div className="blossom-petal" style={{ top: '2px', left: '-4px', transform: 'rotate(288deg)' }} />
              <div className="blossom-center" />
            </div>
          </div>
        ))}
      </div>

      {/* Right cherry blossom tree */}
      <div className="cherry-tree" style={{ right: '5%' }}>
        <div className="tree-trunk" />
        {/* Branches */}
        <div className="tree-branch" style={{ bottom: '180px', right: '10px', width: '120px', height: '15px', transform: 'rotate(25deg)' }} />
        <div className="tree-branch" style={{ bottom: '150px', right: '25px', width: '100px', height: '12px', transform: 'rotate(35deg)' }} />
        <div className="tree-branch" style={{ bottom: '120px', right: '15px', width: '90px', height: '10px', transform: 'rotate(30deg)' }} />
        
        {/* Blossom clusters */}
        {[
          { x: -50, y: 190, delay: 0.3 },
          { x: -75, y: 185, delay: 0.8 },
          { x: -95, y: 175, delay: 1.3 },
          { x: -65, y: 160, delay: 1.8 },
          { x: -85, y: 155, delay: 2.3 },
          { x: -55, y: 130, delay: 2.8 },
          { x: -75, y: 125, delay: 3.3 },
        ].map((cluster, i) => (
          <div
            key={i}
            className="blossom-cluster"
            style={{
              right: `${Math.abs(cluster.x)}px`,
              bottom: `${cluster.y}px`,
              animationDuration: '4s',
              animationDelay: `${cluster.delay}s`,
            }}
          >
            <div className="blossom-flower">
              <div className="blossom-petal" style={{ top: '-6px', left: '6px', transform: 'rotate(0deg)' }} />
              <div className="blossom-petal" style={{ top: '2px', left: '12px', transform: 'rotate(72deg)' }} />
              <div className="blossom-petal" style={{ top: '10px', left: '8px', transform: 'rotate(144deg)' }} />
              <div className="blossom-petal" style={{ top: '10px', left: '0px', transform: 'rotate(216deg)' }} />
              <div className="blossom-petal" style={{ top: '2px', left: '-4px', transform: 'rotate(288deg)' }} />
              <div className="blossom-center" />
            </div>
          </div>
        ))}
      </div>

      {/* Floating hearts */}
      {floatingHearts.map((heart) => (
        <div
          key={heart.id}
          className="floating-heart"
          style={{
            left: `${heart.x}%`,
            top: `${heart.y}%`,
            width: `${heart.size}px`,
            height: `${heart.size}px`,
            animationDuration: `${heart.duration}s`,
            animationDelay: `${heart.delay}s`,
          }}
        >
          <div className="heart-shape" />
        </div>
      ))}

      {/* Falling rose petals */}
      {rosePetals.map((petal) => (
        <div
          key={petal.id}
          className="rose-petal"
          style={{
            left: `${petal.x}%`,
            top: '-50px',
            width: `${petal.size}px`,
            height: `${petal.size}px`,
            animationDuration: `${petal.duration}s`,
            animationDelay: `${petal.delay}s`,
          }}
        >
          <div className="petal-shape" />
        </div>
      ))}

      {/* Romantic candles */}
      <div className="romantic-candle-setup">
        <div className="candle-group">
          <div className="romantic-candle">
            <div className="candle-flame-romantic" style={{ animationDelay: '0s' }} />
            <div className="candle-wax" />
          </div>
          <div className="romantic-candle">
            <div className="candle-flame-romantic" style={{ animationDelay: '0.5s' }} />
            <div className="candle-wax" />
          </div>
          <div className="romantic-candle">
            <div className="candle-flame-romantic" style={{ animationDelay: '1s' }} />
            <div className="candle-wax" />
          </div>
        </div>
      </div>

      {/* Rose bouquet */}
      <div className="rose-bouquet">
        {[
          { x: 30, y: 40, delay: 0 },
          { x: 60, y: 50, delay: 0.5 },
          { x: 90, y: 45, delay: 1 },
          { x: 45, y: 20, delay: 1.5 },
          { x: 75, y: 25, delay: 2 },
        ].map((rose, i) => (
          <div
            key={i}
            className="rose"
            style={{
              left: `${rose.x}px`,
              bottom: `${rose.y}px`,
            }}
          >
            <div className="rose-bloom" style={{ animationDelay: `${rose.delay}s` }} />
            <div className="rose-stem">
              <div className="rose-leaf" style={{ top: '20px', left: '-5px' }} />
              <div className="rose-leaf" style={{ top: '35px', right: '-5px', transform: 'rotate(-45deg)' }} />
            </div>
          </div>
        ))}
      </div>

      {/* Love notes floating */}
      {[
        { x: 15, y: 45, delay: 0 },
        { x: 75, y: 55, delay: 2 },
      ].map((note, i) => (
        <div
          key={i}
          className="love-note"
          style={{
            left: `${note.x}%`,
            top: `${note.y}%`,
            animationDuration: '6s',
            animationDelay: `${note.delay}s`,
          }}
        >
          <div className="note-heart-doodle" />
        </div>
      ))}

      {/* Heart balloons */}
      {[
        { x: 25, y: 20, delay: 0, duration: 8 },
        { x: 70, y: 15, delay: 2, duration: 9 },
      ].map((balloon, i) => (
        <div
          key={i}
          className="heart-balloon"
          style={{
            left: `${balloon.x}%`,
            top: `${balloon.y}%`,
            animationDuration: `${balloon.duration}s`,
            animationDelay: `${balloon.delay}s`,
          }}
        >
          <div className="balloon-heart" />
          <div className="balloon-string" />
        </div>
      ))}

      {/* Sparkles */}
      {Array.from({ length: 30 }, (_, i) => (
        <div
          key={i}
          className="sparkle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 80}%`,
            animationDuration: `${2 + Math.random() * 3}s`,
            animationDelay: `${Math.random() * 3}s`,
          }}
        />
      ))}

      {/* Romantic ground with scattered petals */}
      <div className="romantic-ground">
        {Array.from({ length: 25 }, (_, i) => (
          <div
            key={i}
            className="ground-petal-scatter"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 70}%`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default ValentinesDaySceneOverlay;
